import {
  Box,
  ButtonBase,
  CircularProgress,
  Divider,
  Drawer,
  IconButton,
  Typography,
} from "@mui/material";
import { Icon } from "@iconify/react";
import { useTranslation } from "react-i18next";
import type { Tier } from "../../../services/referrals";

interface PremiumDrawerProps {
  open: boolean;
  onClose: () => void;
  premiumTier: Tier | null;
  standardTier: Tier;
  onPurchase: () => void;
  isPurchasing: boolean;
  isAlreadyPremium: boolean;
}

function findLine(tier: Tier, line: number) {
  return tier.line_percents.find((p) => p.line === line);
}

function formatPct(v: number | undefined | null): string {
  if (v === undefined || v === null || v === 0) return "—";
  return `${v}%`;
}

export function PremiumDrawer({
  open,
  onClose,
  premiumTier,
  standardTier,
  onPurchase,
  isPurchasing,
  isAlreadyPremium,
}: PremiumDrawerProps) {
  const { t } = useTranslation("referral");
  if (!premiumTier) return null;

  const l1 = findLine(premiumTier, 1);
  const stdL1 = findLine(standardTier, 1);

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      sx={{ zIndex: 100010 }}
      PaperProps={{
        sx: {
          borderTopLeftRadius: "24px",
          borderTopRightRadius: "24px",
          padding: "16px 16px 24px",
          background:
            "radial-gradient(140% 100% at 50% 0%, #2C1F5A 0%, #1B1332 60%, #100B1F 100%)",
          color: "#fff",
          maxHeight: "94vh",
        },
      }}
    >
      <Box
        sx={{
          width: "40px",
          height: "4px",
          borderRadius: "2px",
          backgroundColor: "rgba(255,255,255,0.2)",
          margin: "-8px auto 12px",
        }}
      />
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
        }}
      >
        <IconButton onClick={onClose} size="small" sx={{ color: "#fff" }}>
          <Icon icon="solar:close-circle-bold" width={22} height={22} />
        </IconButton>
      </Box>

      <Box
        sx={{
          textAlign: "center",
          paddingTop: "8px",
        }}
      >
        <Icon
          icon="solar:crown-bold-duotone"
          width={72}
          height={72}
          color="#C4A9FF"
        />
        <Typography
          sx={{
            fontSize: "22px",
            fontWeight: 800,
            letterSpacing: "6px",
            marginTop: "6px",
            color: "#EBDCFF",
          }}
        >
          PREMIUM
        </Typography>
      </Box>

      <Box
        sx={{
          marginTop: "16px",
          borderRadius: "18px",
          padding: "14px",
          backgroundColor: "rgba(255,255,255,0.04)",
          border: "1px solid rgba(255,255,255,0.08)",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Icon
              icon="solar:crown-bold-duotone"
              width={18}
              height={18}
              color="#C4A9FF"
            />
            <Typography sx={{ fontSize: "14px", fontWeight: 500 }}>
              {t("premium_drawer.one_payment")}
            </Typography>
          </Box>
          <Box
            sx={{
              padding: "6px 14px",
              background:
                "linear-gradient(135deg, #7A57E8 0%, #4B32AB 100%)",
              borderRadius: "999px",
              fontWeight: 700,
              fontSize: "16px",
            }}
          >
            ${premiumTier.premium_price_usd ?? "0"}
          </Box>
        </Box>
        <Box sx={{ display: "flex", gap: "10px", marginTop: "12px" }}>
          <Box
            sx={{
              flex: 1,
              borderRadius: "12px",
              padding: "12px",
              backgroundColor: "rgba(255,255,255,0.05)",
              textAlign: "center",
            }}
          >
            <Typography sx={{ fontSize: "11px", opacity: 0.7 }}>
              {t("premium_drawer.card_activation")}
            </Typography>
            <Typography sx={{ fontSize: "18px", fontWeight: 700 }}>
              {formatPct(l1?.cardIssue)}
            </Typography>
          </Box>
          <Box
            sx={{
              flex: 1,
              borderRadius: "12px",
              padding: "12px",
              backgroundColor: "rgba(255,255,255,0.05)",
              textAlign: "center",
            }}
          >
            <Typography sx={{ fontSize: "11px", opacity: 0.7 }}>
              {t("premium_drawer.from_transactions")}
            </Typography>
            <Typography sx={{ fontSize: "18px", fontWeight: 700 }}>
              {formatPct(l1?.deposit)}
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          marginTop: "14px",
          borderRadius: "18px",
          padding: "16px",
          backgroundColor: "rgba(255,255,255,0.04)",
          border: "1px solid rgba(255,255,255,0.08)",
        }}
      >
        <Typography sx={{ fontSize: "16px", fontWeight: 700 }}>
          {t("title")}
        </Typography>
        <Box sx={{ display: "flex", gap: "16px", marginTop: "10px" }}>
          <Box sx={{ flex: 1 }}>
            <Typography
              sx={{ fontSize: "28px", fontWeight: 800, color: "#B48DFF" }}
            >
              {formatPct(l1?.cardIssue)}
            </Typography>
            <Typography sx={{ fontSize: "11px", opacity: 0.7 }}>
              {t("premium_drawer.card_activation_single")}
            </Typography>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "4px",
                marginTop: "4px",
                fontSize: "11px",
                opacity: 0.75,
              }}
            >
              <Icon icon="solar:crown-bold-duotone" width={12} height={12} />
              {t("premium_drawer.premium_rate")}
            </Box>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography
              sx={{
                fontSize: "28px",
                fontWeight: 800,
                color: "rgba(255,255,255,0.55)",
              }}
            >
              {formatPct(stdL1?.cardIssue)}
            </Typography>
            <Typography sx={{ fontSize: "11px", opacity: 0.55 }}>
              {t("premium_drawer.card_activation_single")}
            </Typography>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "4px",
                marginTop: "4px",
                fontSize: "11px",
                opacity: 0.55,
              }}
            >
              <Icon icon="solar:user-linear" width={12} height={12} />
              {t("premium_drawer.standard_rate")}
            </Box>
          </Box>
        </Box>

        <Box sx={{ marginTop: "16px", display: "flex", flexDirection: "column", gap: "8px" }}>
          <Typography sx={{ fontSize: "14px", fontWeight: 600 }}>
            {t("premium_drawer.perk_card_2x")}
          </Typography>
          <Typography sx={{ fontSize: "14px", fontWeight: 600 }}>
            {t("premium_drawer.perk_tx_6x")}
          </Typography>
          <Typography sx={{ fontSize: "12px", opacity: 0.7 }}>
            {t("premium_drawer.rate_comparison", {
              premium: formatPct(l1?.deposit),
              standard: formatPct(stdL1?.deposit),
            })}
          </Typography>
        </Box>

        <Divider
          sx={{
            marginY: "16px",
            borderColor: "rgba(255,255,255,0.1)",
            borderStyle: "dashed",
          }}
        />

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "minmax(0,1.2fr) repeat(3, minmax(0,1fr))",
            gap: "8px",
            fontSize: "12px",
          }}
        >
          <Box />
          {[1, 2, 3].map((n) => (
            <Typography
              key={n}
              sx={{ textAlign: "center", opacity: 0.55 }}
            >
              {t("premium_drawer.line_n", { n })}
            </Typography>
          ))}

          {[
            { label: t("premium_drawer.deposits"), field: "deposit" as const },
            { label: t("premium_drawer.card_issue"), field: "cardIssue" as const },
            { label: t("premium_drawer.transfers"), field: "transfer" as const },
          ].map((row) => (
            <Box key={row.field} sx={{ display: "contents" }}>
              <Typography sx={{ fontSize: "12px", opacity: 0.7 }}>
                {row.label}
              </Typography>
              {[1, 2, 3].map((n) => {
                const p = findLine(premiumTier, n);
                const v = p?.[row.field] ?? null;
                return (
                  <Typography
                    key={n}
                    sx={{
                      fontSize: "13px",
                      fontWeight: 600,
                      textAlign: "center",
                    }}
                  >
                    {formatPct(v)}
                  </Typography>
                );
              })}
            </Box>
          ))}
        </Box>
      </Box>

      <ButtonBase
        onClick={onPurchase}
        disabled={isPurchasing || isAlreadyPremium}
        sx={{
          marginTop: "20px",
          width: "100%",
          padding: "16px",
          borderRadius: "16px",
          background:
            "linear-gradient(135deg, #7A57E8 0%, #4B32AB 100%)",
          color: "#fff",
          fontSize: "15px",
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: "0.5px",
          opacity: isPurchasing || isAlreadyPremium ? 0.6 : 1,
          "&:active": { transform: "scale(0.98)" },
        }}
      >
        {isPurchasing ? (
          <CircularProgress size={20} sx={{ color: "#fff" }} />
        ) : isAlreadyPremium ? (
          t("premium_drawer.already_active")
        ) : (
          <>
            {t("premium_drawer.get_premium")}
            <Icon
              icon="solar:arrow-right-linear"
              width={18}
              height={18}
              style={{ marginLeft: "8px" }}
            />
          </>
        )}
      </ButtonBase>
    </Drawer>
  );
}
